import { escapeDqlIdentifier } from '@portal/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SystemCollections, SystemIds } from 'components/dittoAppConnection'
import useRpcClient from 'components/hooks/rpc/useRpcClient'
import { useContext, useMemo } from 'react'

import {
  buildNewPlaygroundAuthObject,
  PLAYGROUND_AUTH_PROVIDER,
} from '../../dittoPortalProvider/defaultAuthSettings'
import AppSettingsContext from '../../scenes/appManagement/context'

// Types of token webhooks. Today we support token liveQueryWebhooks and anonymous.  Anonymous is only used for playground mode
type WebhookType = 'tokenWebhook'

export type WebhookSecret = {
  /** Base64-encoded webhook secret (128 bytes / 1024 bits) */
  secret: string
  /** Validity start time in RFC 3339 format */
  notBefore: string
  /** Validity end time in RFC 3339 format */
  notAfter: string
}

export type AuthWebhookConfiguration = {
  type: WebhookType
  webhookUrl: string
  version?: string
  webhookSecret?: WebhookSecret
}

type AnonymousType = 'anonymous'

export type AnonymousProviderConfiguration = {
  type: AnonymousType
  sharedToken: string
  sessionLength: number
  permission: Record<string, unknown>
}

export type AuthenticationConfig = {
  authenticationEnabled?: boolean
  providers: {
    [webhookName: string]:
      | AuthWebhookConfiguration
      | AnonymousProviderConfiguration
  }
}

export function getWebhookConfig(
  config: AuthenticationConfig | null | undefined,
  providerName: string,
): AuthWebhookConfiguration | undefined {
  switch (config?.providers[providerName]?.type) {
    case 'tokenWebhook':
      return config.providers[providerName] as AuthWebhookConfiguration
    default:
      return undefined
  }
}

export function getPlaygroundProvider(
  config: AuthenticationConfig | null | undefined,
): AnonymousProviderConfiguration | undefined {
  switch (config?.providers[PLAYGROUND_AUTH_PROVIDER]?.type) {
    case 'anonymous':
      return config.providers[
        PLAYGROUND_AUTH_PROVIDER
      ] as AnonymousProviderConfiguration
    default:
      return undefined
  }
}

export type AppAuthenticationReturn = {
  configuration?: AuthenticationConfig | null
  isAuthEnabled?: boolean
  isLoading: boolean
  error: Error | null
  togglePlaygroundMode: () => Promise<void>
  refreshPlaygroundToken: () => Promise<void>
  createWebhook: (name: string, url: string, legacy: boolean) => Promise<void>
  updateWebhook: (
    fromName: string,
    name: string,
    url: string,
    legacy: boolean,
  ) => Promise<void>
  deleteWebhook: (name: string) => Promise<void>
}

type Params = {
  appId?: string
}

const COLLECTION = SystemCollections.APP_WEBHOOKS_COLLECTION
const CONFIG_ID = SystemIds.AUTH_WEBHOOKS_CONFIGURATION

/** React hook for managing authentication webhooks */
const useAppAuthentication = (params: Params = {}): AppAuthenticationReturn => {
  const ctx = useContext(AppSettingsContext)
  const appId = params.appId || ctx.appId
  const appDomain = ctx.cloudDomainUrl

  const { executeDqlQuery, rpcAccessToken } = useRpcClient(appId, appDomain)
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['authConfig', appId], [appId])

  const {
    data: configuration,
    isPending,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await executeDqlQuery(
        `SELECT * FROM ${COLLECTION} WHERE _id = '${CONFIG_ID}'`,
      )
      if (!result.items?.length) return null
      return result.items[0] as unknown as AuthenticationConfig
    },
    enabled: !!rpcAccessToken,
  })

  // This looks to see if an application is in production or playground mode.  This logic can seem a bit confusing
  // because there is literally a flag on the config called "authenticationEnabled" but that is referencing something
  // else.  The config field decides if the big peer is requiring a valid session for access, not if it is playground
  // mode or not
  const isAuthEnabled = !(
    configuration?.providers?.hasOwnProperty(PLAYGROUND_AUTH_PROVIDER) ||
    (configuration?.authenticationEnabled != undefined &&
      configuration?.authenticationEnabled == false)
  )

  const invalidateConfig = () => queryClient.invalidateQueries({ queryKey })

  const assertMutated = (
    result: Awaited<ReturnType<typeof executeDqlQuery>>,
  ) => {
    if (!result.mutatedDocumentIds?.length) {
      throw new Error('No documents were updated')
    }
  }

  const togglePlaygroundMutation = useMutation({
    mutationFn: async () => {
      if (isAuthEnabled) {
        // Currently authenticated, switch to playground
        const result = await executeDqlQuery(
          `UPDATE ${COLLECTION} SET authenticationEnabled = true, providers.${PLAYGROUND_AUTH_PROVIDER} = :provider WHERE _id = '${CONFIG_ID}'`,
          { args: { provider: buildNewPlaygroundAuthObject() } },
        )
        assertMutated(result)
      } else {
        // Currently playground, switch to authenticated
        const result = await executeDqlQuery(
          `UPDATE ${COLLECTION} SET authenticationEnabled = true WHERE _id = '${CONFIG_ID}'`,
        )
        assertMutated(result)
        await executeDqlQuery(
          `UPDATE ${COLLECTION} UNSET providers.${PLAYGROUND_AUTH_PROVIDER} WHERE _id = '${CONFIG_ID}'`,
        )
      }
      await invalidateConfig()
    },
  })

  const refreshPlaygroundMutation = useMutation({
    mutationFn: async () => {
      const result = await executeDqlQuery(
        `UPDATE ${COLLECTION} SET providers.${PLAYGROUND_AUTH_PROVIDER} = :provider WHERE _id = '${CONFIG_ID}'`,
        { args: { provider: buildNewPlaygroundAuthObject() } },
      )
      assertMutated(result)
      await invalidateConfig()
    },
  })

  const createWebhookMutation = useMutation({
    mutationFn: async ({
      name,
      url,
      legacy,
    }: {
      name: string
      url: string
      legacy: boolean
    }) => {
      const provider = {
        type: 'tokenWebhook' as const,
        webhookUrl: url,
        ...(legacy ? {} : { version: '1' }),
      }
      const result = await executeDqlQuery(
        `UPDATE ${COLLECTION} SET providers.${escapeDqlIdentifier(name)} = :provider WHERE _id = '${CONFIG_ID}'`,
        { args: { provider } },
      )
      assertMutated(result)
      await invalidateConfig()
    },
  })

  const updateWebhookMutation = useMutation({
    mutationFn: async ({
      fromName,
      toName,
      url,
      legacy,
    }: {
      fromName: string
      toName: string
      url: string
      legacy: boolean
    }) => {
      if (fromName !== toName) {
        const unsetResult = await executeDqlQuery(
          `UPDATE ${COLLECTION} UNSET providers.${escapeDqlIdentifier(fromName)} WHERE _id = '${CONFIG_ID}'`,
        )
        assertMutated(unsetResult)
      }
      const provider = {
        type: 'tokenWebhook' as const,
        webhookUrl: url,
        ...(legacy ? {} : { version: '1' }),
      }
      const result = await executeDqlQuery(
        `UPDATE ${COLLECTION} SET providers.${escapeDqlIdentifier(toName)} = :provider WHERE _id = '${CONFIG_ID}'`,
        { args: { provider } },
      )
      assertMutated(result)
      if (legacy) {
        await executeDqlQuery(
          `UPDATE ${COLLECTION} UNSET providers.${escapeDqlIdentifier(toName)}.version WHERE _id = '${CONFIG_ID}'`,
        )
      }
      await invalidateConfig()
    },
  })

  const deleteWebhookMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const result = await executeDqlQuery(
        `UPDATE ${COLLECTION} UNSET providers.${escapeDqlIdentifier(name)} WHERE _id = '${CONFIG_ID}'`,
      )
      assertMutated(result)
      await invalidateConfig()
    },
  })

  return {
    configuration: configuration ?? undefined,
    isAuthEnabled,
    isLoading: isPending,
    error: error as Error | null,
    togglePlaygroundMode: () => togglePlaygroundMutation.mutateAsync(),
    refreshPlaygroundToken: () => refreshPlaygroundMutation.mutateAsync(),
    createWebhook: (name: string, url: string, legacy: boolean) =>
      createWebhookMutation.mutateAsync({ name, url, legacy }),
    updateWebhook: (
      fromName: string,
      toName: string,
      url: string,
      legacy: boolean,
    ) => updateWebhookMutation.mutateAsync({ fromName, toName, url, legacy }),
    deleteWebhook: (name: string) =>
      deleteWebhookMutation.mutateAsync({ name }),
  }
}

export default useAppAuthentication
